import { podBulkUploadApi } from '@/api/services'
import {
  BULK_UPLOAD_CHECK_CHUNK_SIZE,
  BULK_UPLOAD_CHECK_CONCURRENCY,
} from '@/utils/bulkUploadConfig'
import type { BulkUploadResumeStatus } from '@/utils/bulkUploadResume'
import { mergeResumeStatuses } from '@/utils/bulkUploadResumeMerge'

const EMPTY_RESUME: BulkUploadResumeStatus = {
  files: {},
  suggestedBatchId: null,
  counts: { done: 0, retry: 0, new: 0 },
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

async function asyncPool<T, R>(limit: number, items: T[], fn: (item: T) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++
      results[i] = await fn(items[i])
    }
  })
  await Promise.all(workers)
  return results
}

function parseResumeResponse(res: unknown): BulkUploadResumeStatus {
  const body = res as { data?: { data?: BulkUploadResumeStatus } & BulkUploadResumeStatus }
  return body?.data?.data ?? (body?.data as BulkUploadResumeStatus) ?? EMPTY_RESUME
}

export async function fetchBulkUploadResumeChunk(
  documentType: string,
  fileNames: string[],
  options?: { chunkSize?: number; concurrency?: number }
): Promise<BulkUploadResumeStatus> {
  if (!fileNames.length) return EMPTY_RESUME

  const chunkSize = options?.chunkSize ?? BULK_UPLOAD_CHECK_CHUNK_SIZE
  const concurrency = options?.concurrency ?? BULK_UPLOAD_CHECK_CONCURRENCY
  const chunks = chunkArray(fileNames, chunkSize)

  if (chunks.length === 1) {
    const res = await podBulkUploadApi.checkResume({ documentType, fileNames: chunks[0] })
    return parseResumeResponse(res)
  }

  const parts = await asyncPool(concurrency, chunks, async (chunk) => {
    const res = await podBulkUploadApi.checkResume({ documentType, fileNames: chunk })
    return parseResumeResponse(res)
  })

  return mergeResumeStatuses(parts)
}
